/**
 * Hydrates deferred product thumbnails when they scroll into the thumb rail.
 *
 * The rail is a native horizontal scroll container (.product__thumbs--scroller,
 * overflow-x: scroll — theme.js drives it via scrollLeft in childNavGoTo), and
 * only ~4 of its items are on screen at a time. loading="lazy" cannot narrow
 * that down: Chrome measures its distance-from-viewport threshold on both axes,
 * and the threshold is wider than the whole rail, so every thumbnail gets
 * fetched. product-images.liquid therefore renders thumbnails past the visible
 * few with data-src/data-srcset instead of src/srcset, and this file swaps them
 * in on demand.
 *
 * IntersectionObserver is rooted on the scroller itself, so it reports
 * horizontal intersection within the rail rather than against the page viewport.
 */
(() => {
  // Both of these are horizontal overflow scrollers whose content is wider than
  // their window, which is the case loading="lazy" cannot handle on its own.
  const SCROLLER_SELECTOR = [
    '[data-product-thumbs] .product__thumbs--scroller',
    '.image-card-picker__track',
  ].join(', ');
  const DEFERRED_SELECTOR = 'img[data-src]';

  // Horizontal runway, so an image starts fetching just before it is dragged
  // into view rather than as it appears.
  //
  // This is measured, not a round guess. The root rect's right edge lands at
  // (window width + margin), and every item whose left edge falls inside it gets
  // hydrated. On a 412px viewport:
  //
  //   thumb rail   372px window, 91px pitch  -> index 5 starts at 455
  //   swatch track 368px window, 107px pitch -> index 4 starts at 428
  //
  // So the ceiling is 83px for the rail but only 60px for the swatch track, and
  // 60 sits exactly on the boundary where subpixel rounding could tip it. 40px
  // clears both with room to spare and still covers everything on screen.
  //
  // Worth re-checking if either component's item width or gap changes. An
  // earlier 300px value quietly cost four extra thumbnails, about 25KB.
  const ROOT_MARGIN = '0px 40px';

  // Grace period after load before the safety net checks whether the observer
  // ever reported anything.
  const FALLBACK_DELAY = 1000;

  const hydrate = (img) => {
    const { src, srcset } = img.dataset;
    if (!src) return;

    // srcset first: setting src on an img that already has a srcset lets the
    // browser pick from the candidates instead of fetching src and swapping.
    if (srcset) {
      img.srcset = srcset;
      delete img.dataset.srcset;
    }

    img.src = src;
    delete img.dataset.src;
  };

  const hydrateAll = (scroller) => {
    for (const img of scroller.querySelectorAll(DEFERRED_SELECTOR)) hydrate(img);
  };

  const observeScroller = (scroller) => {
    // product-images.liquid emits this script once per gallery, so a page with a
    // featured-product section alongside the main one executes it twice. One
    // observer per scroller is enough, so a bound rail is left alone.
    if (scroller.dataset.thumbsLazyBound) return;

    const deferred = [...scroller.querySelectorAll(DEFERRED_SELECTOR)];
    if (!deferred.length) return;

    scroller.dataset.thumbsLazyBound = '1';

    if (!('IntersectionObserver' in window)) {
      hydrateAll(scroller);
      return;
    }

    // IntersectionObserver queues an initial callback for every newly observed
    // target, so this flips true on the first frame in any healthy case. If it
    // is still false after load, the observer is not reporting and the images
    // would stay blank forever — so hydrate them rather than lose the rail.
    let hasReported = false;

    const observer = new IntersectionObserver(
      (entries) => {
        hasReported = true;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          hydrate(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { root: scroller, rootMargin: ROOT_MARGIN }
    );

    // Every deferred thumbnail is observed, including the ones hidden because
    // they belong to another variant's image set. theme.js toggles that `hide`
    // class on variant change, and an element gaining a box is picked up by the
    // next intersection check — so switching colour hydrates the new set with no
    // extra wiring here.
    for (const img of deferred) observer.observe(img);

    const scheduleFallback = () => {
      setTimeout(() => {
        if (hasReported) return;
        hydrateAll(scroller);
      }, FALLBACK_DELAY);
    };

    // Only reachable via the deferred script tag today, so readyState is
    // 'interactive' and the listener is used — but a rail observed after load
    // would otherwise be left with no safety net at all.
    if (document.readyState === 'complete') {
      scheduleFallback();
    } else {
      window.addEventListener('load', scheduleFallback, { once: true });
    }
  };

  const init = () => {
    for (const scroller of document.querySelectorAll(SCROLLER_SELECTOR)) {
      observeScroller(scroller);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
